import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { Bell, MessageCircle, Send, Target, Users, Wallet, CheckCircle2, Clock } from "lucide-react";
import { useApi } from "../hooks/useApi";
import api, { formatError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toLocalISODate } from "../lib/dates";
import { MonthNav, WhatsAppLink, thisMonth } from "./MonthNav";
import { ShareImageButton } from "./ShareImageButton";

const usd = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function Stat({ icon: Icon, label, value, sub, testId }) {
  return (
    <div className="rounded-xl border border-gold/20 bg-parchment-card p-3" data-testid={testId}>
      <p className="flex items-center gap-1 text-[11px] uppercase tracking-wide text-muted-foreground"><Icon size={12} className="text-gold" /> {label}</p>
      <p className="mt-1 font-serif-display text-2xl font-bold text-crimson">{value}</p>
      {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
    </div>
  );
}

function StatsGrid({ d }) {
  return (
    <>
      <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat icon={Target} label="Expected" value={usd(d.expected)} sub={`${d.members_expected} members × ${usd(d.monthly_dues)}`} testId="dues-goal-expected" />
        <Stat icon={Wallet} label="Dues covered" value={usd(d.received)} sub={d.shortfall > 0 ? `${usd(d.shortfall)} to go · ${usd(d.cash_received)} cash in this month` : "Goal reached!"} testId="dues-goal-received" />
        <Stat icon={Users} label="Members paid" value={`${d.members_paid} / ${d.members_expected}`} sub={`${d.pct_members}% of members`} testId="dues-goal-members-paid" />
        <Stat icon={CheckCircle2} label="Progress" value={`${d.pct}%`} sub="of monthly goal" testId="dues-goal-pct" />
      </div>
      <div className="mt-4 h-3 w-full overflow-hidden rounded-full bg-gold/20" role="progressbar" aria-valuenow={d.pct} aria-valuemin={0} aria-valuemax={100} data-testid="dues-goal-bar">
        <div className="h-full rounded-full bg-palm transition-[width] duration-700" style={{ width: `${d.pct}%` }} />
      </div>
      <p className="mt-2 text-xs text-muted-foreground">Counts <strong>dues payments only</strong> (payments into the Dues account), including dues paid in advance in earlier months — registration fees, levies and donations are not deducted or counted. A member is "paid" when their dues payments cover every month from their dues start through {d.month_label}.{d.members_advance ? ` ${d.members_advance} covered ${d.month_label} with advance payments.` : ""} "Cash in" is dues money actually received during the month. Every member on the roster is included{d.record_only ? ` (${d.record_only} without portal login)` : ""}.</p>
    </>
  );
}

function ShareLinks({ d, month }) {
  const summary = useMemo(() => `📊 Dues update — ${d.month_label}\nOkuapemman Association of Massachusetts\n\nExpected: ${usd(d.expected)} (${d.members_expected} members × ${usd(d.monthly_dues)})\nDues received: ${usd(d.received)}\nMembers fully paid: ${d.members_paid} of ${d.members_expected}${d.members_partial ? ` (${d.members_partial} partial)` : ""}\nProgress: ${d.pct}% of goal\n\nThank you to everyone who has paid! If you haven't yet, please pay via the portal or Zelle. Biakoye!`, [d]);
  const paidNames = useMemo(() => `✅ Members who have paid dues — ${d.month_label}:\n${d.paid.filter((p) => p.covers_month).map((p) => `• ${p.name}`).join("\n") || "(none yet)"}\n\nMedaase! Okuapemman Association of Massachusetts`, [d]);
  return (
    <div className="mt-4 flex flex-wrap gap-2">
      <WhatsAppLink text={summary} testId="dues-goal-share"><MessageCircle size={14} /> Share summary on WhatsApp</WhatsAppLink>
      <WhatsAppLink text={paidNames} testId="dues-goal-share-paid" outline><MessageCircle size={14} /> Share list of members who paid</WhatsAppLink>
      <ShareImageButton url={`/admin/dues/monthly-goal/card.png?month=${month}&kind=summary`} filename={`dues-summary-${month}.png`} text={summary} label="Summary as branded image" testId="dues-goal-share-image" />
      <ShareImageButton url={`/admin/dues/monthly-goal/card.png?month=${month}&kind=paid`} filename={`dues-paid-${month}.png`} text={paidNames} label="Paid list as branded image" testId="dues-goal-share-paid-image" />
    </div>
  );
}

function PaidMeta({ r }) {
  const when = r.paid_in_advance ? " · paid in advance" : r.last_paid ? ` · ${toLocalISODate(r.last_paid)}` : "";
  return <span className="ml-2 shrink-0 text-xs text-palm">{usd(r.amount)}{when}{r.covers_month ? "" : " · partial"}{r.months_ahead > 0 ? ` · +${r.months_ahead} mo ahead` : ""}</span>;
}

function UnpaidBadges({ r }) {
  return (
    <>
      {r.partial && <span className="ml-2 rounded-full bg-palm/15 px-2 py-0.5 text-[10px] font-semibold uppercase text-palm" data-testid={`dues-goal-partial-${r.id}`}>partial {usd(r.paid_so_far)}</span>}
      {r.reachable === false && <span className="ml-2 rounded-full bg-gold/20 px-2 py-0.5 text-[10px] font-semibold uppercase text-crimson" data-testid={`dues-goal-nologin-${r.id}`}>no login</span>}
    </>
  );
}

function MemberList({ rows, kind }) {
  if (rows.length === 0) return <p className="text-sm text-muted-foreground">None.</p>;
  return (
    <ul className="max-h-72 space-y-1 overflow-y-auto pr-1" data-testid={`dues-goal-${kind}-list`}>
      {rows.map((r) => (
        <li key={r.id} className="flex items-center justify-between rounded-lg bg-parchment-card px-3 py-2 text-sm" data-testid={`dues-goal-${kind}-${r.id}`}>
          <span className="truncate">{r.name}{r.member_id ? <span className="ml-1 text-xs text-muted-foreground">{r.member_id}</span> : null}{kind === "unpaid" && <UnpaidBadges r={r} />}</span>
          {kind === "paid" && <PaidMeta r={r} />}
        </li>
      ))}
    </ul>
  );
}

function RemindPanel({ month, count, unreachable, last, onSent }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const reachable = count - (unreachable || 0);
  const send = async () => {
    if (!window.confirm(`Send a dues reminder (push + email) to ${reachable} member${reachable === 1 ? "" : "s"} who have not paid yet?`)) return;
    setBusy(true);
    try { const { data } = await api.post("/admin/dues/monthly-goal/remind", { month, note }); toast.success(`Reminder queued for ${data.queued} member(s)`); setNote(""); onSent(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  if (count === 0) return null;
  return (
    <div className="mb-3 rounded-xl border border-crimson/20 bg-crimson/5 p-3" data-testid="dues-remind-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm"><Bell size={14} className="mr-1 inline text-crimson" /> Nudge the {reachable} member{reachable === 1 ? "" : "s"} with portal access who haven't paid yet (push + email).{unreachable ? ` ${unreachable} without login need a call or WhatsApp.` : ""}</p>
        <Button size="sm" onClick={send} disabled={busy} className="gap-1 bg-crimson hover:bg-crimson-hover" data-testid="dues-remind-send"><Send size={13} /> {busy ? "Sending…" : "Send reminder"}</Button>
      </div>
      <Input value={note} onChange={(e) => setNote(e.target.value)} maxLength={400} placeholder="Optional note to include (e.g. Zelle details, deadline)" className="mt-2" data-testid="dues-remind-note" />
      {last && <p className="mt-2 text-xs text-muted-foreground" data-testid="dues-remind-last">Last reminder: {toLocalISODate(last.sent_at)} by {last.sent_by} to {last.count} member(s)</p>}
    </div>
  );
}

function PaidUnpaidTabs({ d, month, reload }) {
  const [tab, setTab] = useState("paid");
  const cls = (active, color) => `-mb-px border-b-2 px-3 py-2 text-sm font-semibold ${active ? `border-${color} text-${color}` : "border-transparent text-muted-foreground"}`;
  return (
    <div className="mt-5">
      <div className="flex gap-2 border-b border-gold/20">
        <button onClick={() => setTab("paid")} className={cls(tab === "paid", "palm")} data-testid="dues-goal-tab-paid"><CheckCircle2 size={14} className="mr-1 inline" />Paid ({d.members_paid})</button>
        <button onClick={() => setTab("unpaid")} className={cls(tab === "unpaid", "crimson")} data-testid="dues-goal-tab-unpaid"><Clock size={14} className="mr-1 inline" />Not yet ({d.members_unpaid})</button>
      </div>
      <div className="mt-3">
        {tab === "paid" ? <MemberList rows={d.paid} kind="paid" /> : (
          <>
            <RemindPanel month={month} count={d.members_unpaid} unreachable={d.unreachable} last={d.last_reminder} onSent={reload} />
            <MemberList rows={d.unpaid} kind="unpaid" />
          </>
        )}
      </div>
    </div>
  );
}

function LoadState({ error, loading, reload }) {
  if (error) {
    const forbidden = error.response?.status === 403;
    return (
      <div className="mt-4 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-crimson/30 bg-crimson/5 p-3 text-sm" data-testid="dues-goal-error">
        <span>{forbidden ? "Your role doesn't have finance access to this snapshot." : "Couldn't load the dues snapshot."}{error.response?.data?.detail ? ` ${formatError(error.response.data.detail)}` : ""}</span>
        {!forbidden && <Button size="sm" variant="outline" onClick={reload} disabled={loading} data-testid="dues-goal-retry">{loading ? "Retrying…" : "Retry"}</Button>}
      </div>
    );
  }
  return <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="dues-goal-skeleton">{[0, 1, 2, 3].map((i) => <div key={i} className="h-24 animate-pulse rounded-xl bg-gold/10" />)}</div>;
}

export function MonthlyDuesGoal() {
  const [month, setMonth] = useState(thisMonth());
  const { data: d, error, loading, reload } = useApi(`/admin/dues/monthly-goal?month=${month}`);
  return (
    <div className="glass rounded-2xl p-6" data-testid="monthly-dues-goal">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="flex items-center gap-2 font-serif-display text-xl font-bold text-palm"><Target size={20} className="text-gold" /> Monthly dues goal</h3>
        <MonthNav month={month} label={d?.month_label} onChange={setMonth} testId="dues-goal" />
      </div>
      {!d ? <LoadState error={error} loading={loading} reload={reload} /> : (
        <>
          <StatsGrid d={d} />
          <ShareLinks d={d} month={month} />
          <PaidUnpaidTabs d={d} month={month} reload={reload} />
        </>
      )}
    </div>
  );
}
